import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/utils/utils';

interface InsightShellProps {
  /** 1-indexed current step. */
  currentStep: number;
  totalSteps: number;
  children: ReactNode;
}

export function InsightShell({
  currentStep,
  totalSteps,
  children,
}: InsightShellProps) {
  return (
    <div className="min-h-screen w-full bg-page-bg bg-[radial-gradient(60%_50%_at_50%_0%,rgba(34,209,197,0.12),transparent_70%),radial-gradient(40%_40%_at_85%_85%,rgba(22,120,194,0.10),transparent_75%)]">
      <div className="mx-auto flex min-h-screen max-w-5xl flex-col px-6 py-6 sm:px-10 sm:py-10">
        <ProgressDots
          currentStep={currentStep}
          totalSteps={totalSteps}
          className="mx-auto mb-6 sm:mb-10"
        />
        <div className="flex-1">{children}</div>
      </div>
    </div>
  );
}

interface ProgressDotsProps {
  currentStep: number;
  totalSteps: number;
  className?: string;
}

function ProgressDots({
  currentStep,
  totalSteps,
  className,
}: ProgressDotsProps) {
  const { t } = useTranslation('onboarding');
  return (
    <div
      className={cn('flex items-center gap-2', className)}
      aria-label={t('insights.progress.step')(currentStep, totalSteps)}
    >
      {Array.from({ length: totalSteps }).map((_, index) => {
        const stepNumber = index + 1;
        const isActive = stepNumber === currentStep;
        const isComplete = stepNumber < currentStep;
        return (
          <span
            key={index}
            className={cn(
              'h-1.5 rounded-full transition-all duration-300',
              isActive && 'w-8 bg-primary',
              isComplete && 'w-4 bg-primary/60',
              !isActive && !isComplete && 'w-4 bg-foreground/10'
            )}
          />
        );
      })}
    </div>
  );
}
